import { Link } from '@tanstack/react-router';
import {
  AtSign,
  BellRing,
  CornerDownRight,
  MoveRight,
  Repeat,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import type { NotificationBody } from '@/shared/api/generated/model';
import { cx } from '@/shared/lib/cx';
import { describeDate, describeWhen, todayISO } from '@/shared/lib/dates';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Avatar } from '@/shared/ui/Avatar';

import styles from './NotificationItem.module.scss';

interface Props {
  workspace: string;
  item: NotificationBody;
  compact?: boolean;
  onOpen: (item: NotificationBody) => void;
}

const ICONS: Record<NotificationBody['kind'], ReactNode> = {
  assigned: <UserPlus />,
  reviewer: <UserCheck />,
  mentioned: <AtSign />,
  replied: <CornerDownRight />,
  status: <MoveRight />,
  due: <BellRing />,
  repeated: <Repeat />,
};

const DAY = 24 * 60 * 60 * 1000;

/** «срок задачи — завтра», «…через 3 дня», «…прошёл 2 дня назад». */
function describeDueNotification(dueDate: string): string {
  const k = t.inbox.kinds;
  const diff = Math.round((Date.parse(dueDate) - Date.parse(todayISO())) / DAY);

  if (Number.isNaN(diff)) {
    return k.dueOn(dueDate);
  }

  if (diff === 0) {
    return k.dueToday;
  }

  if (diff === 1) {
    return k.dueTomorrow;
  }

  if (diff < 0) {
    return diff >= -30 ? k.dueOverdue(-diff) : k.dueOn(describeDate(dueDate));
  }

  return diff <= 30 ? k.dueIn(diff) : k.dueOn(describeDate(dueDate));
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** «Давид назначил вас исполнителем» — глагол по виду уведомления. */
function describeNotification(item: NotificationBody): string {
  const k = t.inbox.kinds;

  switch (item.kind) {
    case 'assigned':
      return k.assigned;
    case 'reviewer':
      return k.reviewer;
    case 'mentioned':
      return item.payload['source'] === 'comment' ? k.mentionedComment : k.mentionedTask;
    case 'replied':
      return k.replied;
    case 'status':
      return k.status(text(item.payload['from']), text(item.payload['to']));
    case 'due':
      return describeDueNotification(text(item.payload['due_date']));
    case 'repeated':
      return k.repeated(text(item.payload['from_key']));
    default:
      return '';
  }
}

/**
 * Строка уведомления: аватар того, кто сделал, глагол, задача, время.
 * Непрочитанное — с точкой и чуть ярче. Клик ведёт на задачу по короткой
 * ссылке и отмечает прочитанным (это делает родитель через onOpen).
 */
export function NotificationItem({ workspace, item, compact = false, onOpen }: Props) {
  // Напоминания шлёт система: вместо человека — имя приложения.
  const actor = item.actor
    ? item.actor.name === ''
      ? item.actor.email
      : item.actor.name
    : item.kind === 'due' || item.kind === 'repeated'
      ? t.inbox.kinds.system
      : '';

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={springs.snappy}
    >
      <Link
        to="/app/w/$slug/t/$key"
        params={{ slug: workspace, key: item.task_key }}
        className={cx(
          styles['item'],
          !item.read && styles['item--unread'],
          compact && styles['item--compact'],
        )}
        onClick={() => {
          onOpen(item);
        }}
      >
        <span className={styles['item__avatar']}>
          <Avatar name={actor === '' ? '?' : actor} size={compact ? 'xs' : 'sm'} />
          <span className={styles['item__kind']}>{ICONS[item.kind]}</span>
        </span>
        <span className={styles['item__body']}>
          <span className={styles['item__text']}>
            <b>{actor}</b> {describeNotification(item)}
          </span>
          <span className={styles['item__task']}>
            <span className={styles['item__key']}>{item.task_key}</span>
            <span className={styles['item__title']}>{item.task_title}</span>
          </span>
        </span>
        <span className={styles['item__meta']}>
          <span className={styles['item__time']}>{describeWhen(item.created_at)}</span>
          {!item.read && <span className={styles['item__dot']} aria-hidden />}
        </span>
      </Link>
    </motion.div>
  );
}
